import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { contact, social, mainNav, type NavItem } from "@/lib/site";
import { useI18n } from "./I18nContext";
import LanguageSwitcher from "./LanguageSwitcher";

// Replaces header.php plus the meanmenu/scroll behaviour from custom.js:
//   - .main-nav gains .menu-shrink past 50px of scroll
//   - below 991px the desktop nav is swapped for an accordion drawer, which is
//     what jquery.meanmenu.js was cloning the markup to build
// Class names are unchanged so style.css / responsive.css / meanmenu.css still
// apply exactly as before.

// style.css only ever styled one level of dropdown, so links in a second-level
// submenu (the lung-cancer stages) inherit a colour that leaves them invisible
// against the white panel. The original header.php carried the same fix as an
// inline style="color:#4a6f8a" on each of those five links.
const SUBMENU_LINK_COLOR = { color: "#4a6f8a" };

function DesktopNavItem({ item, depth = 0 }: { item: NavItem; depth?: number }) {
  const { t, localizeHref } = useI18n();
  const hasChildren = !!item.children?.length;
  const nested = depth >= 2 ? SUBMENU_LINK_COLOR : undefined;

  return (
    <li className="nav-item">
      {hasChildren && !item.href ? (
        <a
          href="#"
          className="nav-link dropdown-toggle"
          style={nested}
          onClick={(e) => e.preventDefault()}
        >
          {t(item.label)}
        </a>
      ) : (
        <Link
          href={localizeHref(item.href!)}
          className={hasChildren ? "nav-link dropdown-toggle" : "nav-link"}
          style={nested}
        >
          {t(item.label)}
        </Link>
      )}
      {hasChildren && (
        <ul className="dropdown-menu">
          {item.children!.map((child) => (
            <DesktopNavItem key={child.label} item={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

function MobileNavItem({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const { t, localizeHref } = useI18n();
  const [open, setOpen] = useState(false);
  const hasChildren = !!item.children?.length;

  return (
    <li className="nav-item">
      <div className="mobile-nav-row">
        {item.href ? (
          <Link href={localizeHref(item.href)} className="nav-link" onClick={onNavigate}>
            {t(item.label)}
          </Link>
        ) : (
          <span className="nav-link">{t(item.label)}</span>
        )}
        {hasChildren && (
          <button
            type="button"
            className={`mean-expand${open ? " mean-clicked" : ""}`}
            aria-expanded={open}
            aria-label={`Toggle ${item.label} submenu`}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "-" : "+"}
          </button>
        )}
      </div>
      {hasChildren && open && (
        <ul className="dropdown-menu mobile-submenu">
          {item.children!.map((child) => (
            <MobileNavItem key={child.label} item={child} onNavigate={onNavigate} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function Header({ localizable = true }: { localizable?: boolean }) {
  const { t, localizeHref } = useI18n();
  const [shrink, setShrink] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const onScroll = () => setShrink(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Close the drawer whenever a route change completes.
  useEffect(() => {
    const close = () => setDrawerOpen(false);
    router.events.on("routeChangeComplete", close);
    return () => router.events.off("routeChangeComplete", close);
  }, [router.events]);

  return (
    <>
      <div className="header-top desktop-image">
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-8 col-lg-8">
              <div className="header-top-item">
                <div className="header-top-left">
                  <ul>
                    <li>
                      <a href={contact.phoneHref}>
                        <i className="icofont-ui-call" /> {t("Call")} : {contact.phone}
                      </a>
                    </li>
                    <li>
                      <a href={`mailto:${contact.email}`}>
                        <i className="icofont-ui-message" /> {t("Email")}: {contact.email}
                      </a>
                    </li>
                    <li>
                      <i className="icofont-location-pin" /> {t(contact.address)}
                    </li>
                    <li>
                      <i className="icofont-ui-timer" /> {t(contact.hours)}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-sm-2 col-lg-2">
              <div className="header-top-item">
                <div className="header-top-right">
                  <ul>
                    {social.map((s) => (
                      <li key={s.label}>
                        <a href={s.href} aria-label={s.label} target="_blank" rel="noopener noreferrer">
                          <i className={s.icon} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-sm-1 col-lg-2">
              {/* Replaces the Google Translate widget: the site now ships real
                  translated routes, and leaving Translate in would fight the
                  React tree for control of the same text.

                  Hidden on pages with no translated counterpart — the blog
                  listing, the 190 legacy posts, and the category/author pages.
                  The live PHP blog carried no Translate widget either, so this
                  matches it. A post authored in the CMS does have translations,
                  and keeps the switcher. */}
              {localizable && <LanguageSwitcher />}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed">
        <a className="call-us" href={contact.whatsapp} aria-label="Chat on WhatsApp">
          <i className="bx bx-tada">
            <img src="/assets/img/whatsappp.png" alt="whatsapp icon" width={50} height={50} style={{ width: 50, height: 50 }} />
          </i>
        </a>
      </div>

      <div className="navbar-area sticky-top">
        <div className="mobile-nav">
          <Link href={localizeHref("/")} className="logo">
            <img src="/assets/img/logotho.webp" alt="Chest Surgery India" height={71} width={213} />
          </Link>
          <button
            type="button"
            className={`mean-bar-toggle${drawerOpen ? " is-open" : ""}`}
            aria-label="Toggle navigation menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
          {drawerOpen && (
            <nav className="mobile-drawer">
              <ul className="navbar-nav">
                {mainNav.map((item) => (
                  <MobileNavItem key={item.label} item={item} onNavigate={() => setDrawerOpen(false)} />
                ))}
              </ul>
            </nav>
          )}
        </div>

        <div className={`main-nav${shrink ? " menu-shrink" : ""}`}>
          <div>
            <nav className="navbar navbar-expand-md" style={{ padding: "0px 20px 0px 20px" }}>
              <Link className="navbar-brand" href={localizeHref("/")}>
                <img src="/assets/img/logotho.png" alt="Chest Surgery India" width={300} height={100} style={{ height: "auto" }} />
              </Link>
              <div className="collapse navbar-collapse mean-menu" id="navbarSupportedContent">
                <ul className="navbar-nav">
                  {mainNav.map((item) => (
                    <DesktopNavItem key={item.label} item={item} />
                  ))}
                </ul>
              </div>
            </nav>
          </div>
        </div>
      </div>

    </>
  );
}
